import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Key, Save, Eye, EyeOff, ShieldCheck, User, KeyRound } from "lucide-react";
import { STORE_CODES as CODES } from "../lib/storeMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Store Login accounts (Super Admin): username, password, enable/disable, trusted devices. */
export default function StoreAccessCodes() {
  const { adminHeaders } = useAuth();
  const [usernames, setUsernames] = useState({});
  const [editUser, setEditUser] = useState({});
  const [active, setActive] = useState({});
  const [lastLogins, setLastLogins] = useState({});
  const [editingCodes, setEditingCodes] = useState({});
  const [busy, setBusy] = useState(null);
  const [showCodes, setShowCodes] = useState({});
  const [pinSet, setPinSet] = useState({});

  useEffect(() => {
    axios.get(`${API}/admin/store-codes`, { headers: adminHeaders() })
      .then(res => {
        setUsernames(res.data.usernames || {}); setEditUser(res.data.usernames || {});
        setActive(res.data.active || {}); setLastLogins(res.data.last_logins || {}); setPinSet(res.data.pin_set || {});
      })
      .catch(err => console.error("Store codes fetch error:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const policyError = (pw) => {
    if (!pw || pw.length < 8) return "Min 8 characters";
    if (!/[A-Z]/.test(pw)) return "Needs an uppercase letter";
    if (!/[a-z]/.test(pw)) return "Needs a lowercase letter";
    if (!/\d/.test(pw)) return "Needs a number";
    if (!/[^A-Za-z0-9]/.test(pw)) return "Needs a special character";
    return null;
  };

  const savePassword = async (storeCode) => {
    const newCode = editingCodes[storeCode];
    const err = policyError(newCode);
    if (err) { toast.error(err); return; }
    setBusy(`pw-${storeCode}`);
    try {
      await axios.put(`${API}/admin/store-codes`, { store_code: storeCode, access_code: newCode }, { headers: adminHeaders() });
      setEditingCodes(prev => ({ ...prev, [storeCode]: "" }));
      setPinSet(prev => ({ ...prev, [storeCode]: true }));
      toast.success(`${storeCode} password updated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    } finally { setBusy(null); }
  };

  const saveUsername = async (storeCode) => {
    const u = (editUser[storeCode] || "").trim().toLowerCase();
    setBusy(`user-${storeCode}`);
    try {
      const r = await axios.put(`${API}/admin/store-codes/username`, { store_code: storeCode, username: u }, { headers: adminHeaders() });
      setUsernames(prev => ({ ...prev, [storeCode]: r.data.username }));
      setEditUser(prev => ({ ...prev, [storeCode]: r.data.username }));
      toast.success(`${storeCode} username is now "${r.data.username}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update username");
    } finally { setBusy(null); }
  };

  const toggleActive = async (storeCode) => {
    const next = !active[storeCode];
    if (!next && !window.confirm(`Disable login for ${storeCode}? Every open session for this store will be signed out.`)) return;
    setBusy(`act-${storeCode}`);
    try {
      await axios.patch(`${API}/admin/store-codes/active`, { store_code: storeCode, active: next }, { headers: adminHeaders() });
      setActive(prev => ({ ...prev, [storeCode]: next }));
      toast.success(`${storeCode} login ${next ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally { setBusy(null); }
  };

  return (
    <Card className="bg-white border border-black/[0.1] shadow-sm rounded-xl" data-testid="store-access-codes">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold tracking-tight flex items-center gap-2">
          <Key size={18} strokeWidth={1.5} className="text-[#0071e3]" />
          Store Logins
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-[#6e6e73] mb-4">Each store signs in with a <span className="font-semibold">username + password</span> on the same login screen as everyone else. Passwords are stored hashed and can't be viewed — only reset. New passwords need 8+ characters with upper &amp; lower case, a number and a special character.</p>
        <div className="divide-y divide-black/[0.06] rounded-xl border border-black/[0.08] overflow-hidden" data-testid="store-codes-management">
          {CODES.map(code => {
            const isVisible = showCodes[code];
            const on = active[code] !== false;
            const userChanged = (editUser[code] || "").trim().toLowerCase() !== (usernames[code] || "");
            return (
              <div key={code} className={`p-3 sm:p-4 ${on ? "" : "bg-[#fbfbfd]"}`} data-testid={`store-login-row-${code}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-12 text-xs font-bold text-[#0071e3] bg-blue-50 px-2 py-1.5 rounded text-center">{code}</span>
                  <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${on ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"}`} data-testid={`store-active-status-${code}`}>
                    {on ? "Login enabled" : "Login disabled"}
                  </span>
                  <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${pinSet[code] ? 'text-emerald-700 bg-emerald-50' : 'text-amber-700 bg-amber-50'}`} data-testid={`store-pin-status-${code}`}>
                    {pinSet[code] ? 'Password set' : 'No password'}
                  </span>
                  <span className="text-xs text-[#86868b] ml-auto hidden sm:block" data-testid={`store-last-login-${code}`}>
                    Last login: {lastLogins[code] ? new Date(lastLogins[code]).toLocaleString() : "Never"}
                  </span>
                  <button
                    type="button" role="switch" aria-checked={on} disabled={busy === `act-${code}`}
                    onClick={() => toggleActive(code)} data-testid={`store-active-toggle-${code}`}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${on ? "bg-[#34c759]" : "bg-[#e5e5ea]"}`}
                    title={on ? "Disable login" : "Enable login"}
                  >
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="flex items-center gap-2">
                    <User size={14} className="text-[#86868b] flex-shrink-0" />
                    <Input
                      data-testid={`store-username-input-${code}`}
                      value={editUser[code] || ""}
                      onChange={(e) => setEditUser(prev => ({ ...prev, [code]: e.target.value }))}
                      className="border-black/[0.12] rounded-lg text-sm h-9"
                      placeholder="Username"
                    />
                    <Button size="sm" disabled={!userChanged || busy === `user-${code}`} onClick={() => saveUsername(code)} data-testid={`save-store-username-${code}`}
                      className={`rounded-full text-xs font-medium h-9 ${userChanged ? 'bg-[#0071e3] hover:bg-[#0060c0] text-white' : 'bg-slate-100 text-slate-400'}`}>
                      <Save size={12} className="mr-1" /> Save
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <KeyRound size={14} className="text-[#86868b] flex-shrink-0" />
                    <div className="relative flex-1">
                      <Input
                        data-testid={`store-code-input-${code}`}
                        type={isVisible ? "text" : "password"}
                        value={editingCodes[code] || ""}
                        onChange={(e) => setEditingCodes(prev => ({ ...prev, [code]: e.target.value }))}
                        className="border-black/[0.12] rounded-lg text-sm pr-8 h-9"
                        placeholder={pinSet[code] ? "New password" : "Set a password"}
                      />
                      <button type="button" onClick={() => setShowCodes(prev => ({ ...prev, [code]: !prev[code] }))} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {isVisible ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    <Button size="sm" disabled={!editingCodes[code] || busy === `pw-${code}`} onClick={() => savePassword(code)} data-testid={`save-store-code-${code}`}
                      className={`rounded-full text-xs font-medium h-9 ${editingCodes[code] ? 'bg-[#1d1d1f] hover:bg-black text-white' : 'bg-slate-100 text-slate-400'}`}>
                      {busy === `pw-${code}` ? "..." : "Set"}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <TrustedDevicesManager adminHeaders={adminHeaders} />
      </CardContent>
    </Card>
  );
}

/** Per-store trusted-device visibility + control (Super Admin). */
function TrustedDevicesManager({ adminHeaders }) {
  const [data, setData] = useState({});   // { code: {enabled, devices, open, loading} }

  const load = async (code) => {
    setData((p) => ({ ...p, [code]: { ...(p[code] || {}), loading: true, open: true } }));
    try {
      const r = await axios.get(`${API}/admin/stores/${code}/trusted-devices`, { headers: adminHeaders() });
      setData((p) => ({ ...p, [code]: { enabled: r.data.enabled, devices: r.data.devices || [], open: true, loading: false } }));
    } catch { toast.error("Failed to load devices"); setData((p) => ({ ...p, [code]: { ...(p[code] || {}), loading: false } })); }
  };
  const toggle = async (code, enabled) => {
    try {
      await axios.post(`${API}/admin/stores/${code}/trusted-devices/toggle`, { enabled }, { headers: adminHeaders() });
      setData((p) => ({ ...p, [code]: { ...(p[code] || {}), enabled } }));
      toast.success(`Trusted-device ${enabled ? "enabled" : "disabled"} for ${code}`);
    } catch { toast.error("Failed to update"); }
  };
  const revoke = async (code, id) => {
    try { await axios.delete(`${API}/admin/stores/${code}/trusted-devices/${id}`, { headers: adminHeaders() }); toast.success("Device revoked"); load(code); }
    catch { toast.error("Failed to revoke"); }
  };
  const reset = async (code) => {
    if (!window.confirm(`Revoke ALL trusted devices for ${code}? Every device will need manager re-verification.`)) return;
    try { const r = await axios.post(`${API}/admin/stores/${code}/trusted-devices/reset`, {}, { headers: adminHeaders() }); toast.success(`Reset ${r.data.count} device(s)`); load(code); }
    catch { toast.error("Failed to reset"); }
  };

  return (
    <div className="mt-6 pt-5 border-t border-black/[0.08]" data-testid="trusted-devices-manager">
      <p className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-2"><ShieldCheck size={16} className="text-[#0071e3]" /> Trusted devices (store logins)</p>
      <p className="text-xs text-[#6e6e73] mb-3">When enabled for a store, a PIN login from a new device needs a one-time manager (Area/Super) code. Admin 2FA is unaffected.</p>
      <div className="space-y-2">
        {CODES.map((code) => {
          const d = data[code] || {};
          return (
            <div key={code} className="border border-black/[0.08] rounded-lg" data-testid={`td-store-${code}`}>
              <div className="flex items-center gap-2 px-3 py-2">
                <span className="w-12 text-xs font-bold text-[#0071e3] bg-blue-50 px-2 py-1 rounded text-center">{code}</span>
                <Button size="sm" variant="outline" className="text-xs rounded-full" data-testid={`td-open-${code}`}
                  onClick={() => (d.open ? setData((p) => ({ ...p, [code]: { ...d, open: false } })) : load(code))}>
                  {d.open ? "Hide" : "Manage"}
                </Button>
                {d.open && (
                  <label className="flex items-center gap-1.5 text-xs font-medium ml-1 cursor-pointer" data-testid={`td-toggle-${code}`}>
                    <input type="checkbox" checked={!!d.enabled} onChange={(e) => toggle(code, e.target.checked)} />
                    {d.enabled ? "Enabled" : "Disabled"}
                  </label>
                )}
                {d.open && d.enabled && (d.devices?.length > 0) && (
                  <Button size="sm" variant="ghost" className="text-xs text-rose-600 ml-auto" data-testid={`td-reset-${code}`} onClick={() => reset(code)}>Reset all</Button>
                )}
              </div>
              {d.open && (
                <div className="px-3 pb-3">
                  {d.loading ? <p className="text-xs text-slate-400">Loading…</p>
                    : !d.enabled ? <p className="text-xs text-slate-400">Feature off — this store logs in with PIN only.</p>
                    : (d.devices?.length ? (
                      <ul className="space-y-1.5" data-testid={`td-list-${code}`}>
                        {d.devices.map((dev) => (
                          <li key={dev.id} className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2 py-1.5">
                            <span className="font-semibold text-slate-700 truncate max-w-[10rem]">{dev.label}</span>
                            <span className="text-slate-400 truncate hidden sm:block flex-1">{(dev.user_agent || "").slice(0, 40)}</span>
                            <span className="text-slate-400">seen {dev.last_seen_at ? new Date(dev.last_seen_at).toLocaleDateString() : "—"}</span>
                            <button className="text-rose-600 hover:underline ml-auto" data-testid={`td-revoke-${dev.id}`} onClick={() => revoke(code, dev.id)}>Revoke</button>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="text-xs text-slate-400">No trusted devices yet.</p>)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
